import MapLayerManager from "./MapLayerManager"
import { InspectorPanel } from "./inspector-panel/InspectorPanel"
import { ImagePreviewModalV2 } from "plugins/shared/ImagePreviewModal"
import PubsubPanel from "panels/overlay/PubsubPanel"
import { PluginPanelsContainer } from "plugins/PluginPanelsContainer"
import { FC, useCallback, useState, memo, useMemo } from "react"
import { MapPortalContext } from "./layers/MapPortalContext"
import { zIndex } from "@nova/constants"
import classNames from "classnames"
import { getUIControllers } from "plugins/plugin-registry"
import { useEntityIdDeepLink } from "./useEntityIdDeepLink"
import { MapLegend } from "./legend/MapLegend"
import { useAppSelector } from "integrations/redux/hooks"
import { selectIsSidePanelCollapsed } from "panels/content/side-panel/slice.new"

export interface LeftModalOptions {
    order?: number
    fullscreen?: boolean
}

const MapProvider = memo(() => {
    const [items, setItems] = useState<{ key: string; element: FC; options: LeftModalOptions }[]>(
        []
    )
    const isSidePanelCollapsed = useAppSelector(selectIsSidePanelCollapsed)

    const register = useCallback((key: string, element: FC, options = {} as LeftModalOptions) => {
        setItems((prev) => {
            if (prev.find((v) => v.key === key)?.element === element) {
                return prev
            }
            const filtered = prev.filter((i) => i.key !== key)
            return [...filtered, { key, element, options }].sort(
                (a, b) => (a.options?.order || 0) - (b.options?.order || 0)
            )
        })
    }, [])

    const unregister = useCallback((key: string) => {
        setItems((prev) => {
            if (!prev.find((v) => v.key === key)) {
                return prev
            }
            return prev.filter((i) => i.key !== key)
        })
    }, [])

    const controllers = useMemo(() => {
        return getUIControllers()
    }, [])

    useEntityIdDeepLink()

    return (
        <MapPortalContext.Provider value={{ register, unregister, items }}>
            <div className="flex relative transition-transform duration-500 ease-in-out w-full h-full mb-2 mr-2 overflow-hidden">
                <MapLayerManager />
                <div
                    className="flex gap-2 justify-end absolute p-2 w-full h-full top-0 right-0 pointer-events-none [&>*]:pointer-events-auto"
                    style={{ zIndex: zIndex.InspectorPanel }}
                >
                    <InspectorPanel />
                </div>

                <div
                    className={classNames(
                        "absolute top-2 pointer-events-none [&>*]:pointer-events-auto",
                        isSidePanelCollapsed ? "left-24" : "left-2"
                    )}
                    style={{ zIndex: zIndex.InspectorPanel }}
                >
                    <MapLegend viewType="project" />
                </div>

                <div
                    className="absolute flex flex-col w-full h-full pointer-events-none overflow-hidden"
                    style={{ zIndex: zIndex.InspectorPanel }}
                >
                    {items.map((i) => (
                        <div
                            className={classNames("[&>*]:pointer-events-auto", {
                                "ml-2 mt-2": !i.options.fullscreen,
                            })}
                            key={i.key}
                        >
                            <i.element />
                        </div>
                    ))}

                    <ImagePreviewModalV2 />
                    <PubsubPanel />
                    <PluginPanelsContainer />
                </div>
            </div>

            <div className="hidden">
                {controllers.map(({ Controller, name }) => (
                    <Controller key={name} />
                ))}
            </div>
        </MapPortalContext.Provider>
    )
})
MapProvider.displayName = "MapProvider"
export default MapProvider
